/**
 * OCDL's four divisions, named after Ogerpon's masks, each with its artwork (static/art/<division>.jpg) and its mask
 * (static/masks/<division>.png: PK Shuffle-style icons by loran_hemlock). A Discord league is a division's when its
 * Showdown room is "<division>division" (tealdivision, hearthflamedivision, ...).
 */
import type { ComponentChildren } from 'preact';
import { Link } from './router';

export const DIVISIONS = [
	{ id: 'teal', name: 'Teal', tagline: 'Moss, vine and the quiet green' },
	{ id: 'wellspring', name: 'Wellspring', tagline: 'Where the falls never rest' },
	{ id: 'hearthflame', name: 'Hearthflame', tagline: 'Forged where the mountain burns' },
	{ id: 'cornerstone', name: 'Cornerstone', tagline: 'Stone that does not move' },
] as const;
export type Division = typeof DIVISIONS[number];

export function divisionOf(league: { room: string | null }): Division | null {
	const id = /^(teal|wellspring|hearthflame|cornerstone)division$/.exec(league.room ?? '')?.[1];
	return DIVISIONS.find(division => division.id === id) ?? null;
}

export const artwork = (division: Division) => `/art/${division.id}.jpg`;
export const maskIcon = (division: Division) => `/masks/${division.id}.png`;

/** The division's Ogerpon mask; without a division, an original, simplified oni mask in the current colour */
export function Mask({ class: className = 'mask', division }: { class?: string, division?: Division }) {
	if (division) return <img class={className} src={maskIcon(division)} alt="" aria-hidden="true" />;
	return <svg class={className} viewBox="0 0 64 64" aria-hidden="true">
		<path fill="currentColor" d="M14 6l8 12c3-1 6-2 10-2s7 1 10 2l8-12 3 18c3 5 4 10 4 15 0 13-11 21-25 21S7 52 7 39c0-5 1-10 4-15z" />
		<path fill="rgb(0 0 0 / .55)" d="M17 34l11 3-1 5-10-3zm30 0l-11 3 1 5 10-3zM24 49c5 3 11 3 16 0l-2 4c-4 2-8 2-12 0z" />
	</svg>;
}

/** A division's artwork across the top of its league's page, with the league's name over it */
export function DivisionBanner({ division, children }: { division: Division, children: ComponentChildren }) {
	return <div class="division-banner" data-division={division.id}>
		<img src={artwork(division)} alt="" onError={event => (event.currentTarget as HTMLImageElement).classList.add('missing')} />
		<div class="division-banner-title"><Mask division={division} />
			<div>{children}<p>{division.tagline}</p></div></div>
	</div>;
}

/** A division's artwork as a tile linking to its league */
export function DivisionTile({ division, href, note }: { division: Division, href: string, note?: string }) {
	return <Link href={href} class="division-tile" data-division={division.id}>
		<img src={artwork(division)} alt="" loading="lazy" onError={event => (event.currentTarget as HTMLImageElement).classList.add('missing')} />
		<span class="division-tile-label"><Mask division={division} /><span><strong>{division.name}</strong>{note && <small>{note}</small>}</span></span>
	</Link>;
}
